import { useEffect, useRef } from "react";
import { Button, notification } from "antd";
import { APP_VERSION } from "../utils/constants";

const VERSION_NOTIFICATION_KEY = "app-version-update";
const VERSION_CHECK_INTERVAL_MS = 60_000; // 1 minute
const VERSION_NOTIFIED_STORAGE_KEY = "pos_version_update_notified";

/**
 * Returns true if a is a newer semantic version than b (e.g. "6.2.0" > "6.1.9").
 */
function isNewerVersion(a, b) {
  if (!a || !b) return false;
  const parse = (v) => v.split(".").map(Number);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return true;
    if (na < nb) return false;
  }
  return false;
}

/**
 * Periodically checks `/version.json` for a newer app version.
 * When a new version is detected, shows a persistent notification
 * with a "Refresh" button that reloads the page.
 * Only notifies once per new version per session (survives refresh).
 */
export const useVersionChecker = () => {
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const checkVersion = async () => {
      try {
        // Cache-bust so we always get the latest version (avoids stale CDN/browser cache)
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
          headers: { Pragma: "no-cache", "Cache-Control": "no-cache" },
        });

        if (!response.ok) return;

        const data = await response.json().catch(() => null);
        const latestVersion = data?.version;

        if (!latestVersion || !isNewerVersion(latestVersion, APP_VERSION)) {
          // On latest version or server behind: clear "notified" so future updates will show again
          try {
            sessionStorage.removeItem(VERSION_NOTIFIED_STORAGE_KEY);
          } catch {
            // ignore
          }
          return;
        }

        // Already shown for this new version in this session (e.g. after refresh)
        try {
          if (sessionStorage.getItem(VERSION_NOTIFIED_STORAGE_KEY) === latestVersion) {
            return;
          }
        } catch {
          // ignore
        }

        if (!isMounted || hasNotifiedRef.current) {
          return;
        }

        hasNotifiedRef.current = true;
        try {
          sessionStorage.setItem(VERSION_NOTIFIED_STORAGE_KEY, latestVersion);
        } catch {
          // ignore
        }

        notification.info({
          key: VERSION_NOTIFICATION_KEY,
          message: "New version available",
          description:
            "A new version of the application is available. Please refresh to get the latest updates.",
          duration: 0,
          placement: "bottomRight",
          btn: (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                // Full reload to ensure the latest assets are loaded
                window.location.reload();
              }}
            >
              Refresh now
            </Button>
          ),
        });
      } catch {
        // Silently ignore version check errors – this should never break the app
      }
    };

    // Initial check shortly after mount
    checkVersion();
    // Periodic checks while the app is open
    const intervalId = window.setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      notification.destroy(VERSION_NOTIFICATION_KEY);
    };
  }, []);
};

